/**
 * CallScreen — full-screen overlay shown during an active/incoming call.
 *
 * Shows:
 *  - Remote video + local PiP video
 *  - Mute / camera toggle / hang-up controls
 *  - Incoming call ring UI (answer / reject)
 */

import React, { useEffect, useRef } from 'react';
import type { CallState } from '../hooks/useWebRTC';

interface Props {
  callState:    CallState;
  localStream:  MediaStream | null;
  remoteStream: MediaStream | null;
  isMuted:      boolean;
  isCameraOff:  boolean;
  remoteUserId: string | null;
  incomingCall: { from: string; callLogId: string; hasVideo: boolean } | null;
  onAnswer:     () => void;
  onReject:     () => void;
  onHangUp:     () => void;
  onToggleMute: () => void;
  onToggleCamera: () => void;
}

export const CallScreen: React.FC<Props> = ({
  callState,
  localStream,
  remoteStream,
  isMuted,
  isCameraOff,
  remoteUserId,
  incomingCall,
  onAnswer,
  onReject,
  onHangUp,
  onToggleMute,
  onToggleCamera,
}) => {
  const localVideoRef  = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  // Bind streams to video elements
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  // ── Incoming ring screen ─────────────────────────────────────────
  if (callState === 'ringing' && incomingCall) {
    return (
      <div className="call-screen call-screen--ringing">
        <div className="call-screen__ring-info">
          <div className="call-screen__ring-avatar">📞</div>
          <p className="call-screen__ring-label">Incoming {incomingCall.hasVideo ? 'video' : 'voice'} call</p>
          <p className="call-screen__ring-from">{incomingCall.from}</p>
        </div>
        <div className="call-screen__ring-actions">
          <button className="btn btn--answer" onClick={onAnswer}>Answer</button>
          <button className="btn btn--reject" onClick={onReject}>Decline</button>
        </div>
      </div>
    );
  }

  // ── Outgoing (waiting for answer) ────────────────────────────────
  if (callState === 'calling') {
    return (
      <div className="call-screen call-screen--calling">
        <p className="call-screen__status">Calling {remoteUserId}…</p>
        <button className="btn btn--hangup" onClick={onHangUp}>Cancel</button>
      </div>
    );
  }

  // ── Active call ──────────────────────────────────────────────────
  if (callState === 'active') {
    return (
      <div className="call-screen call-screen--active">
        {/* Remote video (large) */}
        <video
          ref={remoteVideoRef}
          className="call-screen__remote-video"
          autoPlay
          playsInline
        />

        {/* Local video (picture-in-picture) */}
        <video
          ref={localVideoRef}
          className="call-screen__local-video"
          autoPlay
          playsInline
          muted
        />

        {/* Controls */}
        <div className="call-screen__controls">
          <button
            className={`btn btn--icon ${isMuted ? 'btn--active' : ''}`}
            title={isMuted ? 'Unmute' : 'Mute'}
            onClick={onToggleMute}
          >
            {isMuted ? '🔇' : '🎤'}
          </button>
          <button
            className="btn btn--hangup"
            title="Hang up"
            onClick={onHangUp}
          >
            📵
          </button>
          <button
            className={`btn btn--icon ${isCameraOff ? 'btn--active' : ''}`}
            title={isCameraOff ? 'Camera on' : 'Camera off'}
            onClick={onToggleCamera}
          >
            {isCameraOff ? '📷' : '🎥'}
          </button>
        </div>
      </div>
    );
  }

  // Idle / ended — render nothing
  return null;
};
